import React from 'react';

import Application from './Application.js';

/**
 * Internal React Context - DO NOT use directly in components!
 * 
 * This context wraps the Application.js class, which is framework-agnostic
 * and doesn't know about React. To bridge imperative Application updates
 * to React's declarative model, always use the `useApplication` hook instead.
 * 
 * Architecture:
 * - Application.js: Framework-agnostic business logic
 * - AppContext: React adapter layer (internal)
 * - useApplication: Public API that connects app.refreshUI() to React re-renders
 */
const AppContext = React.createContext();

const application = new Application();
application.load();

/**
 * Custom hook to access the Application instance with automatic re-rendering.
 * 
 * ALWAYS use this hook instead of React.useContext(AppContext) directly!
 * 
 * Architecture:
 * - Application.js calls this.onRefreshUI() (framework-agnostic)
 * - AppContextProvider increments uiVersion state
 * - All components using this hook re-render automatically via context
 * 
 * @returns {Application} The singleton Application instance
 */
export const useApplication = () => {
	const { app, uiVersion } = React.useContext(AppContext);
	
	// The uiVersion dependency ensures components re-render when refreshUI() is called
	// eslint-disable-next-line no-unused-vars
	const _version = uiVersion;
	
	return app;
};



export const AppContextProvider = React.memo(({ children, router }) => {
	const app = React.useRef(application).current;
	const [uiVersion, setUiVersion] = React.useState(0);

	React.useEffect(() => {
		// Set up the refresh callback - when app calls refreshUI(), increment version to trigger re-renders
		app.onRefreshUI = () => {
			setUiVersion(prev => {
				const newVersion = prev + 1;
				console.log('onRefreshUI triggered, updating UI version to:', newVersion);
				return newVersion;
			});
		};

		const hashHandler = () => {
			const path = window.location.hash.substring(1); //remove hash
			app.navigate(path);
		};

		app.onNavigate = (path) => {
			window.location.hash = path;
			router.navigate('/' + path);
		};

		window.addEventListener('hashchange', hashHandler);
		window.location.hash = ''; //init hash
		router.navigate('/' + app.pageId);

		return () => {
			window.removeEventListener('hashchange', hashHandler);
		};
	}, [app, router]);

	return (
		<AppContext.Provider value={{ app, uiVersion }}>
			{children}
		</AppContext.Provider>
	);
});

AppContextProvider.displayName = 'AppContextProvider';
